import { GridColDef } from "@mui/x-data-grid-pro";
import { IconButton, IconContainerProps, Rating, styled } from "@mui/material";

import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';

/**
 * Icons
 */

const customIcons: {
  [index: string]: {
    textColor: string;
    icon: React.ReactElement<unknown>;
    label: string;
  };
} = {
  1: {
    textColor: 'text-red-500',
    icon: <SentimentVeryDissatisfiedIcon className="text-red-500" />,
    label: 'Very Dissatisfied',
  },
  2: {
    textColor: 'text-orange-500',
    icon: <SentimentDissatisfiedIcon className="text-orange-500" />,
    label: 'Dissatisfied',
  },
  3: {
    textColor: 'text-yellow-500',
    icon: <SentimentSatisfiedIcon className="text-yellow-500" />,
    label: 'Neutral',
  },
  4: {
    textColor: 'text-green-300',
    icon: <SentimentSatisfiedAltIcon className="text-green-300" />,
    label: 'Satisfied',
  },
  5: {
    textColor: 'text-green-600',
    icon: <SentimentVerySatisfiedIcon className="text-green-600" />,
    label: 'Very Satisfied',
  },
};

function IconContainer(props: IconContainerProps) {
  const { value, ...other } = props;
  const isSelected = other.className?.includes('MuiRating-iconFilled');
  return (
    <span {...other}>
      <div className="flex flex-col items-center">
        <span className="flex">
          {customIcons[value].icon}
        </span>
        <span className={`${isSelected ? customIcons[value].textColor : ''} text-xs`}>
          {typeof value === 'number' && value}
        </span>
      </div >
    </span >
  );
}

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconEmpty .MuiSvgIcon-root': {
    color: theme.palette.action.disabled,
  },
}));

/**
 * GenerateHabilidadeItemsColumnsDefinition
 */

type Props = {
  onChangeHabilidadeItem: (idHabilidadeItem: number, nota: number) => void,
  onEditar: (idHabilidadeItem: number) => void,
  onDeletar: (idHabilidadeItem: number) => void,
};

export const GenerateHabilidadeItemsColumnsDefinition = ({
  onChangeHabilidadeItem,
  onEditar,
  onDeletar,
}: Props): GridColDef[] => {
  return [
    {
      field: "habilidade",
      filterable: false,
      headerName: "Habilidade",
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
    {
      field: "notaSelecionada",
      filterable: false,
      headerName: "Nota Selecionada",
      width: 200,
      sortable: false,
      align: 'center',
      valueGetter: (_, rows) => {
        return [rows['idHabilidadeItem'] as number, rows['notaSelecionada'] as number];
      },
      renderCell: ({ value }) => {
        return (
          <StyledRating
            className="w-full h-full"
            getLabelText={(value: number) => customIcons[value].label}
            highlightSelectedOnly={true}
            value={value[1]}
            slotProps={{
              root: {
                className: "flex flex-row justify-center items-center gap-2",
              },
              icon: {
                component: IconContainer,
              }
            }}
            onChange={(_, newNota) => onChangeHabilidadeItem(value[0], newNota ?? 0)}
          />
        );
      },
    },
    {
      field: "nota",
      filterable: false,
      headerName: "Nota",
      width: 100,
      sortable: false,
      align: 'center',
      renderCell: ({ value }) => {
        return (
          value && (
            <div className="flex flex-col justify-center items-center w-full h-full">
              <div className="flex flex-col items-center">
                <span className="flex">
                  {customIcons[value].icon}
                </span>
                <span className={`${customIcons[value].textColor} text-xs`}>
                  {typeof value === 'number' && value}
                </span>
              </div>
            </div>
          )
        );
      },
    },
    {
      field: "acoes",
      filterable: false,
      headerName: "Ações",
      sortable: false,
      valueGetter: (_, rows) => rows['idHabilidadeItem'] as number,
      renderCell: ({ value }) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton onClick={() => onEditar(value)}>
              <DynamicIcon nameIcon="EditRounded" size={25} className="dark:text-white text-primary-green" />
            </IconButton>

            <IconButton onClick={() => onDeletar(value)}>
              <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
            </IconButton>
          </div>
        );
      }
    },
  ];
}